/**
 * <s-product-image-zoom> — PDP fullscreen image zoom with sideways browsing.
 *
 * Gallery buttons open one shared native dialog declaratively via commandfor/show-modal.
 * This component:
 *   - remembers which photo was clicked and scrolls the zoom track to it when the dialog opens;
 *   - handles the --zoom-prev / --zoom-next commands and the arrow keys to move between photos;
 *   - keeps the "n / total" counter in sync while shoppers swipe;
 *   - double tap / double click on a photo zooms it 2.5x at that point (drag to pan); double tap again to zoom out;
 *   - warms the high-resolution zoom image on hover/focus so it is ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][data-zoom-target][commandfor] — gallery trigger; data-zoom-target is the media id.
 *   dialog[data-zoom-dialog] [data-zoom-track]         — horizontal scroll-snap track inside the dialog.
 *   [data-zoom-slide="<media id>"] img[data-zoom-image] — one slide per photo.
 *   [data-zoom-current]                                 — optional counter of the visible photo.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #scrollFrame = 0;
  #lastTap = { time: 0, x: 0, y: 0 };
  #zoomedSlide = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleTriggerClick, opts);
    this.addEventListener('command', this.#handleCommand, opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.#dialog?.addEventListener('close', () => this.#resetZoom(), opts);
    this.#track?.addEventListener('click', this.#handleTap, opts);

    const track = this.#track;
    if (track) track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#scrollFrame);

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  get #dialog() {
    return this.querySelector('dialog[data-zoom-dialog]');
  }

  get #track() {
    return this.querySelector('[data-zoom-track]');
  }

  get #slides() {
    const track = this.#track;
    return track ? Array.from(track.querySelectorAll('[data-zoom-slide]')) : [];
  }

  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-zoom-preload]');
    if (!trigger || !this.contains(trigger)) return;

    const mediaId = trigger.getAttribute('data-zoom-target');
    const slide = this.#slides.find((item) => item.getAttribute('data-zoom-slide') === mediaId);
    if (!slide) return;

    this.#loadSlide(slide);
    // The dialog opens after the click's default command runs; jump to the photo on the next frame.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#goTo(this.#slides.indexOf(slide), 'instant'));
    });
  };

  #handleCommand = (event) => {
    if (event.command === '--zoom-next') this.#step(1);
    if (event.command === '--zoom-prev') this.#step(-1);
  };

  #handleKeydown = (event) => {
    if (!this.#dialog?.open) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(isRtl ? -1 : 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(isRtl ? 1 : -1);
    }
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#updateCounter());
  };

  #currentIndex() {
    const track = this.#track;
    if (!track || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #step(direction) {
    const slides = this.#slides;
    if (slides.length < 2) return;

    const next = (this.#currentIndex() + direction + slides.length) % slides.length;
    this.#goTo(next, 'smooth');
  }

  #goTo(index, behavior) {
    this.#resetZoom();
    const track = this.#track;
    const slides = this.#slides;
    const slide = slides[index];
    if (!track || !slide) return;

    this.#loadSlide(slide);
    if (slides[index + 1]) this.#loadSlide(slides[index + 1]);
    if (slides[index - 1]) this.#loadSlide(slides[index - 1]);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const left = index * track.clientWidth * (isRtl ? -1 : 1);
    track.scrollTo({ left, behavior: reduceMotion ? 'instant' : behavior });
    this.#updateCounter(index);
  }

  #updateCounter(index = this.#currentIndex()) {
    const counter = this.querySelector('[data-zoom-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  #loadSlide(slide) {
    const image = slide.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
  }

  #handleTap = (event) => {
    const slide = event.target instanceof Element ? event.target.closest('[data-zoom-slide]') : null;
    if (!slide) return;

    const now = performance.now();
    const last = this.#lastTap;
    const isDoubleTap = now - last.time < 350 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 40;
    this.#lastTap = isDoubleTap ? { time: 0, x: 0, y: 0 } : { time: now, x: event.clientX, y: event.clientY };
    if (!isDoubleTap) return;

    if (this.#zoomedSlide === slide) {
      this.#resetZoom();
    } else {
      this.#zoomIn(slide, event.clientX, event.clientY);
    }
  };

  #zoomIn(slide, clientX, clientY) {
    this.#resetZoom();
    const image = slide.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    const zoom = 2.5;
    const box = slide.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || box.width;
    const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || box.height;
    const fit = Math.min(box.width / naturalWidth, box.height / naturalHeight);
    const fitWidth = naturalWidth * fit;
    const fitHeight = naturalHeight * fit;
    const fitLeft = box.left + (box.width - fitWidth) / 2;
    const fitTop = box.top + (box.height - fitHeight) / 2;
    const ratioX = Math.min(Math.max((clientX - fitLeft) / fitWidth, 0), 1);
    const ratioY = Math.min(Math.max((clientY - fitTop) / fitHeight, 0), 1);
    const zoomedWidth = fitWidth * zoom;
    const zoomedHeight = fitHeight * zoom;

    // Block layout + overflow lets the zoomed photo pan in both directions; overscroll stays inside the photo.
    slide.style.display = 'block';
    slide.style.overflow = 'auto';
    slide.style.overscrollBehavior = 'contain';
    slide.style.cursor = 'zoom-out';
    image.style.width = `${zoomedWidth}px`;
    image.style.height = `${zoomedHeight}px`;
    image.style.maxWidth = 'none';
    image.style.margin = `${Math.max((box.height - zoomedHeight) / 2, 0)}px ${Math.max((box.width - zoomedWidth) / 2, 0)}px`;
    this.#track?.style.setProperty('overflow-x', 'hidden');
    this.#zoomedSlide = slide;

    slide.scrollLeft = ratioX * zoomedWidth - (clientX - box.left);
    slide.scrollTop = ratioY * zoomedHeight - (clientY - box.top);
  }

  #resetZoom() {
    const slide = this.#zoomedSlide;
    if (!slide) return;

    const image = slide.querySelector('img[data-zoom-image]');
    slide.style.removeProperty('display');
    slide.style.removeProperty('overflow');
    slide.style.removeProperty('overscroll-behavior');
    slide.style.removeProperty('cursor');
    if (image instanceof HTMLImageElement) {
      image.style.removeProperty('width');
      image.style.removeProperty('height');
      image.style.removeProperty('max-width');
      image.style.removeProperty('margin');
    }
    this.#track?.style.removeProperty('overflow-x');
    this.#zoomedSlide = null;
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const mediaId = source.getAttribute('data-zoom-target');
    if (!mediaId || this.#preloaded.has(mediaId)) return;

    const slide = this.#slides.find((item) => item.getAttribute('data-zoom-slide') === mediaId);
    const image = slide?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(mediaId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
